import React from "react";
import { FaCheck, FaPaperPlane, FaTimes } from "react-icons/fa";
import { Drawer } from "../work/WorkUi";
import { AI_SHORTLIST_THRESHOLD, reached } from "./aiHireStages";
import { AiScoreBadge, PreviewButton, StageChip, StageStepper, fmtDate } from "./AiHireUi";

function Field({ label, children }) {
  return (
    <div className="aihire-field">
      <span>{label}</span>
      <b>{children}</b>
    </div>
  );
}

/** Read-only candidate profile with the Ai Hire screening and interview report. */
function CandidateDrawer({ app, onClose }) {
  const { candidate: c, job, aiInterview: ai } = app;
  const canShortlist = !reached(app.stage, "shortlisted") && app.stage !== "rejected";
  return (
    <Drawer title={c.name} onClose={onClose}>
      <div className="aihire-drawer">
        <p className="aihire-drawer__job">
          {job.title} · {job.client} / {job.project} <StageChip stage={app.stage} />
        </p>
        <StageStepper stage={app.stage} />

        {app.rejectReason ? (
          <p className="aihire-note aihire-note--bad">
            <strong>Reason:</strong> {app.rejectReason}
          </p>
        ) : null}

        <h4>Profile</h4>
        <div className="aihire-fields">
          <Field label="Email">{c.email}</Field>
          <Field label="Phone">{c.phone}</Field>
          <Field label="Location">{c.location}</Field>
          <Field label="Experience">{c.experienceYears} years</Field>
          <Field label="Notice period">{c.noticeDays ? `${c.noticeDays} days` : "Immediate"}</Field>
          <Field label="Applied on">{fmtDate(app.appliedOn)}</Field>
        </div>
        <div className="aihire-tags">
          {c.skills.map((s) => (
            <span key={s} className="aihire-tag">
              {s}
            </span>
          ))}
        </div>

        <h4>Interview preferences</h4>
        <div className="aihire-fields">
          <Field label="Locations">{c.preferredLocations.join(", ")}</Field>
          <Field label="Available">{c.availability.label}</Field>
        </div>

        <h4>AI screening</h4>
        <p className="aihire-inline">
          <AiScoreBadge score={app.aiScreenScore} /> Resume match against the job&apos;s skills.
        </p>

        <h4>AI interview</h4>
        {ai ? (
          <div className="aihire-ai">
            <div className="aihire-ai__head">
              <AiScoreBadge score={ai.score} recommendation={ai.recommendation} />
              <span>
                {ai.durationMinutes} min async interview
                {app.stage === "ai_interviewed" && ai.score >= AI_SHORTLIST_THRESHOLD
                  ? " · Ai Hire suggests shortlisting"
                  : ""}
              </span>
            </div>
            <ul className="aihire-bars">
              {ai.skills.map((s) => (
                <li key={s.name}>
                  <span>{s.name}</span>
                  <span className="aihire-bar">
                    <span style={{ width: `${s.score}%` }} />
                  </span>
                  <b>{s.score}</b>
                </li>
              ))}
            </ul>
            <div className="aihire-ai__cols">
              <div>
                <h5>Strengths</h5>
                <ul>
                  {ai.strengths.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h5>Concerns</h5>
                <ul>
                  {ai.concerns.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
            <h5>Transcript highlights</h5>
            {ai.highlights.map((h) => (
              <blockquote key={h.q} className="aihire-quote">
                <p>
                  <strong>Q:</strong> {h.q}
                </p>
                <p>
                  <strong>A:</strong> {h.a}
                </p>
              </blockquote>
            ))}
          </div>
        ) : (
          <p className="aihire-muted">AI interview not taken yet.</p>
        )}

        <div className="aihire-drawer__actions">
          {!ai && app.stage !== "rejected" ? (
            <PreviewButton icon={FaPaperPlane} primary>
              Send AI interview
            </PreviewButton>
          ) : null}
          {canShortlist ? (
            <PreviewButton icon={FaCheck} primary={Boolean(ai)}>
              Shortlist
            </PreviewButton>
          ) : null}
          {app.stage !== "rejected" ? <PreviewButton icon={FaTimes}>Reject</PreviewButton> : null}
        </div>
      </div>
    </Drawer>
  );
}

export default CandidateDrawer;
